import { QueryClient, useQuery } from '@tanstack/react-query'
import { Coin } from '@cosmjs/stargate'
import { useChain as useCosmosKitChain } from '@cosmos-kit/react'
import type { WalletAccount } from '@cosmos-kit/core'
import { useSelectedDenom } from '@/contexts/denom'
import type { SelectedCoinDenom } from '@/wallet-utils/types'
import { assert } from '@/utils'
import { CHAIN_INFO_LIST, CHAIN_CONFIG, getDexInfoFromDenom } from '@/config'
import { queryKeys } from '@/query-keys'
import { z } from 'zod'

// Mapping from SelectedCoinDenom to cosmos-kit chain_name
// @TODO: Move to @/config/chain-config.ts - move it as part of CHAIN_CONFIG
const DENOM_TO_CHAIN_NAME: Record<SelectedCoinDenom, string> = {
  ATOM: 'cosmoshub',
  SCRT: 'secret',
  STRD: 'stride',
  JUNO: 'juno',
  OSMO: 'osmosis',
  STARS: 'stargaze',
  LUNA: 'terra',
  EVMOS: 'evmos',
  INJ: 'injective',
  UMEE: 'umee',
  CRE: 'crescent',
  CMDX: 'comdex',
  IBCX: 'osmosis',
  KUJI: 'kujira',
  USK: 'kujira',
  IST: 'agoric',
  SWTH: 'carbon',
  USC: 'carbon',
  SOMM: 'sommelier',
  BLD: 'agoric',
  TIA: 'celestia',
  DYDX: 'dydx',
  DYM: 'dymension',
  SAGA: 'saga',
  ISLM: 'haqq',
  BAND: 'bandchain'
}

// Wrap cosmos-kit useChain with denom → chain_name mapping
// Fix preferred sign type
export const useChain = (denom: SelectedCoinDenom) => {
  const chainName = DENOM_TO_CHAIN_NAME[denom]

  const chain = useCosmosKitChain(chainName)

  // At the moment, you can't dynamically set a preferred signer type on the provider level
  // if you need data like isNanoLedger from the wallet account
  const getSigningStargateClient = async () => {
    const account = await chain.getAccount()
    if (account.isNanoLedger) {
      chain.chainWallet?.initOfflineSigner('amino')
    }
    return chain.getSigningStargateClient()
  }

  return { ...chain, getSigningStargateClient }
}

export const useStrideChain = () => {
  return useChain('STRD')
}

export const useChainBalance = (denom: SelectedCoinDenom) => {
  const { address, status, getStargateClient } = useChain(denom)

  return useQuery({
    queryKey: queryKeys.chainBalances({ denom, address: address ?? '' }),
    enabled: status === 'Connected',
    queryFn: async (): Promise<{ balances: Coin[]; signature: string }> => {
      assert(address, 'Address is required')
      const client = await getStargateClient()
      const balances = await client.getAllBalances(address)
      return { balances: [...balances], signature: getCoinListSignature(balances) }
    }
  })
}

// Allow dependent hooks to use this function to get a deterministic signature (id) of the coin list
// Would make it simpler to pass it to a query key so that the dependent query is reactive of changes
export const getCoinListSignature = (coins: readonly Coin[]) => {
  if (!coins.length) return 'empty'
  return coins
    .map((c) => [c.denom, c.amount] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)) // deterministic
    .map(([denom, amount]) => `${denom}:${amount}`)
    .join('|')
}

export const useStrideChainBalance = () => {
  const [denom] = useSelectedDenom()

  const query = useChainBalance('STRD')

  if (query.data == null) {
    return query
  }

  const hostIbcDenom = CHAIN_CONFIG[denom].ibcDenom
  const hostMinimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom
  const stMinimalDenom = `st${hostMinimalDenom}`

  const view = {
    signature: query.data.signature,
    balances: query.data.balances,
    balance: query.data.balances.find((c) => c.denom === hostIbcDenom),
    stBalance: query.data.balances.find((c) => c.denom === stMinimalDenom),
    strd: query.data.balances.find((c) => c.denom === 'ustrd')
  }

  return { ...query, data: view }
}

export const useSelectedChain = () => {
  const [denom] = useSelectedDenom()
  return useChain(denom)
}

export const useSelectedChainBalance = () => {
  const [denom] = useSelectedDenom()

  const query = useChainBalance(denom)

  if (query.data == null) {
    return query
  }

  const hostMinimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

  const stIbcDenom = CHAIN_CONFIG[denom].stakedIbcDenom

  const view = {
    signature: query.data.signature,
    balances: query.data.balances,
    balance: query.data.balances.find((c) => c.denom === hostMinimalDenom),
    stBalance: stIbcDenom ? query.data.balances.find((c) => c.denom === stIbcDenom) : undefined
  }

  return { ...query, data: view }
}

export const useDexChain = () => {
  const [denom] = useSelectedDenom()
  const dexInfo = getDexInfoFromDenom(denom)
  return useChain(dexInfo?.coinDenom ?? 'STRD')
}

export const useChainAccount = (denom: SelectedCoinDenom) => {
  const { address, wallet, status, getAccount } = useChain(denom)

  return useQuery<WalletAccount>({
    queryKey: queryKeys.chainAccountV2({ denom, address: address ?? '', wallet }),
    enabled: status === 'Connected',
    queryFn: async () => {
      const account = await getAccount()
      return account
    }
  })
}

export const useStrideChainAccount = () => {
  return useChainAccount('STRD')
}

export const useSelectedChainAccount = () => {
  const [denom] = useSelectedDenom()
  return useChainAccount(denom)
}

const CoinSchema = z.object({ denom: z.string(), amount: z.string() });

const ChainBalanceSchema = z.object({
  balances: CoinSchema.array()
})

// @WARNING: If chainBalances changes, please make sure to keep this in-sync, otherwise it would break
export const getCachedChainBalances = (
  queryClient: QueryClient,
  params: { denom: SelectedCoinDenom; address: string }
) => {
  const cached = queryClient.getQueryData<Coin[]>(queryKeys.chainBalances(params))
  return ChainBalanceSchema.parse(cached)
}
